import { type } from 'arktype';
import { calendarSlotUpdateSchema, updateCalendarSchema } from './calendar.model';
import { locationEntrySchema } from './work-location.model';

const absencePortionLiteral = "'full'|'fraction'|'hours'|'range'";

const timedSlotDataFields = {
  startTime: 'string',
  endTime: 'string',
  'isAllDay?': 'boolean',
  'text?': 'string',
  'icon?': 'string',
} as const;

const locationSlotDataFields = {
  entries: locationEntrySchema.array(),
  'text?': 'string',
  'icon?': 'string',
} as const;

const externalBookingSlotDataSchema = type({
  type: "'external-booking'",
  ...timedSlotDataFields,
  'customProperties?': type('Record<string, unknown>').configure({
    description:
      'Custom values of this booking, keyed by custom-property definition `key` and validated against the tenant `calendar-slot` definitions. Some are required depending on `meetingType` via the definition `config.requiredForBookingTypes`; violations return 400 `Invalid custom property values`.',
    examples: [
      {
        customerName: 'Ada Lovelace',
        phoneNumber: '+41 79 000 00 00',
        emailAddress: 'ada@example.com',
        meetingType: 'Video call',
        destinationSkillIds: ['3f2b8c1e-6d4a-4b7e-9a1c-5e8d2f0a7b34'],
        requiredLanguages: ['de', 'en'],
      },
    ],
  }),
});

const teamMeetingSlotDataSchema = type({
  type: "'team-meeting'",
  ...timedSlotDataFields,
});

const workLocationSlotDataSchema = type({
  type: "'work-location'",
  ...locationSlotDataFields,
});

const locationOverrideSlotDataSchema = type({
  type: "'location-override'",
  ...locationSlotDataFields,
});

const absenceSlotDataSchema = type({
  type: "'absence'",
  absenceType: 'string',
  portion: absencePortionLiteral,
  isAllDay: 'boolean',
  'startTime?': 'string',
  'endTime?': 'string',
  'fractionValue?': 'number',
  'durationMinutes?': 'number',
  'source?': 'string',
});

const openSlotDataSchema = type({
  type: 'string',
  '[string]': 'unknown',
});

export const calendarSlotWriteDataSchema = externalBookingSlotDataSchema
  .or(teamMeetingSlotDataSchema)
  .or(workLocationSlotDataSchema)
  .or(locationOverrideSlotDataSchema)
  .or(absenceSlotDataSchema)
  .or(openSlotDataSchema);

export const calendarSlotWriteSchema = calendarSlotUpdateSchema.merge({
  'data?': calendarSlotWriteDataSchema,
});

/** Documents the PATCH body with typed slot writes. Server validation still runs on `updateCalendarSchema`. */
export const updateCalendarBodyDocSchema = updateCalendarSchema.merge({
  'slots?': type({ '[string]': calendarSlotWriteSchema }).or('null'),
});
